import { useFormContext, Controller } from "react-hook-form";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Consultation, BodyAnalysis } from "@/types/consultation";
import { useEffect, useMemo, useState } from "react";
import { differenceInYears, parseISO } from "date-fns";
import { useGetBodyAnalysisQuery } from "@/services/consultationApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { HistoryFilter } from "../history-filter";
import { HistoryConsultation } from "@/types/consultation";

const MAX_RATE = 100;

export const sections = [
  { title: "Cardio Test",       key: "cardio"      },
  { title: "Endurance Test",    key: "endurance"   },
  { title: "Flexibility Test",  key: "flexibility" },
] as const;

// ── Auto-rank logic ───────────────────────────────────────────────────────────
const getRankFromRate = (rate: number | undefined | null): string => {
  if (rate == null || isNaN(Number(rate))) return "";
  const n = Number(rate);
  if (n <= 25)  return "Poor";
  if (n <= 50)  return "Fair";
  if (n <= 75)  return "Good";
  return "Excellent";           // 76–100 and above
};

// Color per rank for the badge
const RANK_COLOR: Record<string, string> = {
  Poor:      "bg-red-100 text-red-600",
  Fair:      "bg-yellow-100 text-yellow-600",
  Good:      "bg-blue-100 text-blue-600",
  Excellent: "bg-green-100 text-green-700",
};

// ── Validate single rate field ────────────────────────────────────────────────
const validateRate = (value: string): string | null => {
  if (!value || value.trim() === "") return null; // Empty is OK (optional)
  
  const num = parseFloat(value);
  
  if (isNaN(num)) {
    return "Please enter a valid number";
  }
  
  if (num < 0) {
    return "Rate cannot be negative";
  }
  
  if (num === 0) {
    return "Rate must be greater than 0";
  }

  if (num > MAX_RATE) {
    return `Rate cannot exceed ${MAX_RATE}`;
  }
  
  return null; // Valid
};

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

const BodyAnalysisStep = ({ action, open }: FormStepProps) => {
  const { register, watch, setValue, getValues } = useFormContext<Consultation>();
  const memberDob    = watch("member_dob");
  const consultationId = watch("id");
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  // Error tracking for rate fields: { [key]: errorMessage }
  const [rateErrors, setRateErrors] = useState<Record<string, string>>({});

  // Fetch existing analysis whenever consultationId is available
  const { data: analysisData } = useGetBodyAnalysisQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || !open }
  );

  // Populate form from API — use only the latest entry per body_test_type
  useEffect(() => {
    if (analysisData?.data && Array.isArray(analysisData.data)) {
      // Group by body_test_type and pick the one with the highest id
      const latestByType = analysisData.data.reduce<Record<string, typeof analysisData.data[0]>>(
        (acc, item) => {
          const existing = acc[item.body_test_type];
          if (!existing || item.id > existing.id) {
            acc[item.body_test_type] = item;
          }
          return acc;
        },
        {}
      );

      Object.values(latestByType).forEach((item) => {
        const key = item.body_test_type as "cardio" | "endurance" | "flexibility";

        if (item.body_test_rate !== undefined) {
          setValue(`body_analysis.${key}.rate`, item.body_test_rate, { shouldDirty: false });
          const rank = getRankFromRate(item.body_test_rate);
          if (rank) setValue(`body_analysis.${key}.rank`, rank, { shouldDirty: false });
        }

        if (item.age) {
          setValue(`body_analysis.${key}.age`, item.age, { shouldDirty: false });
        }
      });
    }
  }, [analysisData, setValue]);

  // Calculate age from DOB
  const calculatedAge = useMemo(() => {
    if (!memberDob) return null;
    try {
      const age = differenceInYears(new Date(), parseISO(memberDob));
      return isNaN(age) ? null : age;
    } catch {
      return null;
    }
  }, [memberDob]);

  // Prefill age fields when empty/zero
  useEffect(() => {
    if (open && calculatedAge !== null) {
      const current = getValues();
      sections.forEach((section) => {
        const currentAge = current.body_analysis?.[section.key]?.age;
        if (!currentAge || currentAge === 0 || isNaN(currentAge as number)) {
          setValue(`body_analysis.${section.key}.age`, calculatedAge, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      });
    }
  }, [open, calculatedAge, setValue, getValues]);

  // Watch all rate fields so rank badges re-render on change
  const cardioRate      = watch("body_analysis.cardio.rate");
  const enduranceRate   = watch("body_analysis.endurance.rate");
  const flexibilityRate = watch("body_analysis.flexibility.rate");

  const rateByKey: Record<string, number | undefined> = {
    cardio:      cardioRate,
    endurance:   enduranceRate,
    flexibility: flexibilityRate,
  };

  // ── Handle rate input change with validation ───────────────────────────────
  const handleRateChange = (key: string, value: string) => {
    const error = validateRate(value);
    setRateErrors((prev) => {
      const updated = { ...prev };
      if (error) {
        updated[key] = error;
      } else {
        delete updated[key];
      }
      return updated;
    });
  };

  // Keep rank field in sync whenever rate changes
  useEffect(() => {
    sections.forEach(({ key }) => {
      const rate = rateByKey[key];
      const rank = getRankFromRate(rate);
      setValue(`body_analysis.${key}.rank`, rank, { shouldDirty: false });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardioRate, enduranceRate, flexibilityRate, setValue]);

  const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);
  const memberId = watch("member_id");

  return (
    <div className="space-y-4">
      {/* <HistoryFilter memberId={memberId!} onDataFound={setHistoryData} /> */}

      {historyData && (
        <Card className="p-4 border-[#e2e8f0]">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-sm text-[#1e293b]">Previous Analysis Results</p>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">
              Historical View
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {historyData.body_test_analysis.map((test) => (
              <div key={test.body_test_type} className="bg-[#f8fafc] border border-[#f1f5f9] p-3 rounded-lg">
                <p className="text-xs font-bold text-[#64748b] uppercase mb-2 border-b border-[#e2e8f0] pb-1">
                  {test.body_test_type}
                </p>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-[#64748b]">Rate:</span>
                  <span className="font-semibold text-[#1e293b]">{test.body_test_rate ?? "N/A"}</span>
                </div>
                <div className="flex justify-between items-center text-xs mt-1">
                  <span className="text-[#64748b]">Rank:</span>
                  <span className="font-semibold text-[#10b981]">{test.body_test_rank ?? "N/A"}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="mb-4">
          <p className="font-medium text-sm">Body Analysis</p>
          <p className="text-xs text-gray-500 mt-1">
            Enter the Rate (0–100) for each test — Rank will be calculated automatically.
          </p>
        </div>
        <Separator className="h-[1px] mb-2" />

        <div className="space-y-6">
          {sections.map((section) => {
            const rate = rateByKey[section.key];
            const rank = getRankFromRate(rate);
            const rankColorClass = rank ? RANK_COLOR[rank] : "bg-gray-100 text-gray-400";
            const rateFieldError = rateErrors[section.key];

            return (
              <div key={section.key} className="space-y-4">
                <h3 className="text-sm font-semibold text-[#1e293b] border-b-2 border-[#6be27f] w-fit pb-1">
                  {section.title}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2 items-end">
                  {/* Age — read only */}
                  <div className="w-full">
                    <FloatingLabelInput
                      label="Age"
                      type="number"
                      disabled={true}
                      {...register(`body_analysis.${section.key}.age`, { valueAsNumber: true })}
                    />
                  </div>

                  {/* Rate — coach enters this */}
                  <div className="w-full flex flex-col gap-1.5">
                    <FloatingLabelInput
                      label="Rate (0–100)"
                      type="number"
                      step="1"
                      min="0"
                      max={MAX_RATE}
                      inputMode="numeric"
                      pattern="\d*"
                      {...register(`body_analysis.${section.key}.rate`, {
                        valueAsNumber: true,
                      })}
                      onChange={(e) => handleRateChange(section.key, e.target.value)}
                    />
                    {/* Inline Error Message */}
                    {rateFieldError && (
                      <div className="flex items-start gap-2">
                        <i className="fa fa-exclamation-circle text-red-500 text-xs mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-red-600 font-medium">{rateFieldError}</span>
                      </div>
                    )}
                  </div>

                  {/* Rank — auto-calculated, read only */}
                  <div className="w-full flex flex-col justify-end gap-1">
                    <label className="text-[12px] text-[#64748b] font-medium px-0.5 leading-none">
                      Rank
                    </label>
                    <div
                      className={`flex items-center justify-between px-3 py-2 rounded-md border text-sm font-semibold h-10 ${rankColorClass} border-transparent`}
                    >
                      <span>{rank || "—"}</span>
                      {rank && (
                        <span className="text-[10px] font-normal opacity-70">auto</span>
                      )}
                    </div>
                    {/* Hidden input keeps rank in form state for submission */}
                    <input
                      type="hidden"
                      {...register(`body_analysis.${section.key}.rank`)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Rank legend */}
        <div className="mt-6 flex flex-wrap gap-3 text-xs text-gray-500">
          {[
            { label: "0–25 → Poor",      cls: "bg-red-100 text-red-600"        },
            { label: "26–50 → Fair",      cls: "bg-yellow-100 text-yellow-600"  },
            { label: "51–75 → Good",      cls: "bg-blue-100 text-blue-600"      },
            { label: "76–100 → Excellent",cls: "bg-green-100 text-green-700"    },
          ].map(({ label, cls }) => (
            <span key={label} className={`px-2 py-0.5 rounded-full font-medium ${cls}`}>
              {label}
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default BodyAnalysisStep;